import { difference, isEmpty, uniq } from "lodash";
import { ProcessModelCopyLevel } from "./process-model-copy.model";

/** A task of a source tree, see {@link groupSelectionByLevel}. */
export interface ProcessModelCopyTreeTask {
  modelId: string;
}

/** A phase of a source tree, see {@link groupSelectionByLevel}. */
export interface ProcessModelCopyTreePhase {
  modelId: string;
  tasks?: readonly ProcessModelCopyTreeTask[];
}

/** An area of a source tree, see {@link groupSelectionByLevel}. */
export interface ProcessModelCopyTreeArea {
  modelId: string;
  phases?: readonly ProcessModelCopyTreePhase[];
}

/** A source phase that becomes a new phase, with only its selected tasks. */
export interface ProcessModelCopySelectedPhase {
  modelId: string;
  taskModelIds: string[];
}

/** A source area that becomes a new area, with only the phases that have selected tasks. */
export interface ProcessModelCopySelectedArea {
  modelId: string;
  phases: ProcessModelCopySelectedPhase[];
}

/** What a selection of tasks turns into at a level, see {@link groupSelectionByLevel}. */
export interface ProcessModelCopySelection {
  level: ProcessModelCopyLevel;
  /** The new areas, at area level only. */
  areas: ProcessModelCopySelectedArea[];
  /** The new phases, at phase level only. */
  phases: ProcessModelCopySelectedPhase[];
  /** Every selected task in source tree order, at every level. */
  taskModelIds: string[];
  /** Selected modelIds that are no task of the source tree. */
  unknownTaskModelIds: string[];
}

/**
 * Which new areas or phases a selection of tasks creates at the given level, so the wizard's summary and the planned
 * copy (`createCopyPlan`) agree: at phase level every source phase with at least one selected task becomes one new phase (in source
 * order, with only the selected tasks), at area level every source area does, with only its phases that have selected
 * tasks. Tasks keep their source order at every level.
 */
export function groupSelectionByLevel(
  areas: readonly ProcessModelCopyTreeArea[],
  taskModelIds: readonly string[],
  level: ProcessModelCopyLevel
): ProcessModelCopySelection {
  const selectedModelIds = new Set(taskModelIds);
  const seenModelIds = new Set<string>();
  const isSelectedOnce = (modelId: string): boolean => {
    if (!selectedModelIds.has(modelId) || seenModelIds.has(modelId)) {
      return false;
    }

    seenModelIds.add(modelId);
    return true;
  };

  const selectedAreas = (areas ?? [])
    .map((area) => ({
      modelId: area.modelId,
      phases: (area.phases ?? [])
        .map((phase) => ({
          modelId: phase.modelId,
          taskModelIds: (phase.tasks ?? []).map(({ modelId }) => modelId).filter(isSelectedOnce),
        }))
        .filter((phase) => !isEmpty(phase.taskModelIds)),
    }))
    .filter((area) => !isEmpty(area.phases));

  const selectedPhases = selectedAreas.flatMap((area) => area.phases);
  return {
    level,
    areas: level === ProcessModelCopyLevel.Area ? selectedAreas : [],
    phases: level === ProcessModelCopyLevel.Phase ? selectedPhases : [],
    taskModelIds: selectedPhases.flatMap((phase) => phase.taskModelIds),
    unknownTaskModelIds: difference(uniq(taskModelIds), Array.from(seenModelIds)),
  };
}
